import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../../../api/client';
import {
  PRIMARY,
  TEXT_DARK,
  TEXT_MUTED,
  BORDER_LIGHT,
  BG_SURFACE,
  BG_CARD,
  FONT_HEADING,
} from '../designTokens';

interface AssignmentRule {
  id: string;
  name: string;
  description: string;
  ruleType: string;
  priority: number;
  enabled: boolean;
}

const RULE_TYPES = ['KeepOrderTogether', 'KeepFamilyTogether', 'OverflowToAdjacent'];

const styles: Record<string, React.CSSProperties> = {
  container: {},
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
  },
  title: {
    fontSize: '0.95rem',
    fontFamily: FONT_HEADING,
    fontWeight: 600,
    color: TEXT_DARK,
    margin: 0,
  },
  addBtn: {
    padding: '0.45rem 0.9rem',
    fontSize: '0.8rem',
    fontWeight: 600,
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  card: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.75rem 1rem',
    backgroundColor: BG_CARD,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '6px',
    marginBottom: '0.5rem',
    cursor: 'grab',
    transition: 'box-shadow 0.15s, border-color 0.15s',
  },
  cardDragging: {
    opacity: 0.5,
    borderColor: PRIMARY,
    boxShadow: '0 2px 8px rgba(26,107,60,0.15)',
  },
  cardDragOver: {
    borderColor: PRIMARY,
    backgroundColor: '#f0fdf4',
  },
  priorityBadge: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    backgroundColor: BG_SURFACE,
    border: `1px solid ${BORDER_LIGHT}`,
    fontSize: '0.75rem',
    fontWeight: 700,
    color: TEXT_DARK,
    flexShrink: 0,
  },
  cardContent: {
    flex: 1,
    minWidth: 0,
  },
  cardName: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: TEXT_DARK,
    margin: 0,
  },
  cardDesc: {
    fontSize: '0.75rem',
    color: TEXT_MUTED,
    margin: '0.15rem 0 0 0',
  },
  ruleTypeBadge: {
    display: 'inline-block',
    fontSize: '0.68rem',
    fontWeight: 600,
    padding: '0.15rem 0.4rem',
    borderRadius: '3px',
    backgroundColor: '#e3f2fd',
    color: '#1565c0',
    marginTop: '0.25rem',
  },
  cardActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.35rem',
    flexShrink: 0,
  },
  iconBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px 6px',
    fontSize: '0.9rem',
    borderRadius: '4px',
    lineHeight: 1,
  },
  toggle: {
    position: 'relative' as const,
    width: '36px',
    height: '20px',
    borderRadius: '10px',
    cursor: 'pointer',
    border: 'none',
    transition: 'background-color 0.2s',
    padding: 0,
  },
  toggleDot: {
    position: 'absolute' as const,
    top: '3px',
    width: '14px',
    height: '14px',
    borderRadius: '50%',
    backgroundColor: '#fff',
    transition: 'left 0.2s',
  },
  form: {
    padding: '1rem',
    backgroundColor: BG_SURFACE,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '6px',
    marginBottom: '1rem',
  },
  formRow: {
    display: 'flex',
    gap: '0.75rem',
    marginBottom: '0.75rem',
    alignItems: 'flex-end',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.25rem',
    flex: 1,
  },
  label: {
    fontSize: '0.72rem',
    fontWeight: 600,
    color: TEXT_MUTED,
    textTransform: 'uppercase' as const,
  },
  input: {
    padding: '0.4rem 0.6rem',
    fontSize: '0.82rem',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '4px',
    outline: 'none',
  },
  select: {
    padding: '0.4rem 0.6rem',
    fontSize: '0.82rem',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '4px',
    outline: 'none',
    backgroundColor: '#fff',
  },
  formActions: {
    display: 'flex',
    gap: '0.5rem',
  },
  saveBtn: {
    padding: '0.4rem 0.8rem',
    fontSize: '0.8rem',
    fontWeight: 600,
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  cancelBtn: {
    padding: '0.4rem 0.8rem',
    fontSize: '0.8rem',
    fontWeight: 500,
    backgroundColor: '#e0e0e0',
    color: '#333',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  error: {
    padding: '0.5rem 0.75rem',
    color: '#d32f2f',
    fontSize: '0.8rem',
    backgroundColor: '#fdecea',
    borderRadius: '4px',
    marginBottom: '0.75rem',
  },
  loading: {
    textAlign: 'center' as const,
    padding: '2rem',
    color: TEXT_MUTED,
    fontSize: '0.85rem',
  },
  empty: {
    textAlign: 'center' as const,
    padding: '3rem 1rem',
    color: TEXT_MUTED,
    fontSize: '0.85rem',
    backgroundColor: BG_SURFACE,
    borderRadius: '6px',
    border: `1px dashed ${BORDER_LIGHT}`,
  },
};

export function RuleManagementSection() {
  const [rules, setRules] = useState<AssignmentRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingRule, setEditingRule] = useState<AssignmentRule | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // Form state
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formRuleType, setFormRuleType] = useState(RULE_TYPES[0]);
  const [formPriority, setFormPriority] = useState(1);

  const fetchRules = useCallback(async () => {
    try {
      const data = await apiClient<AssignmentRule[]>('/batch-assignment/rules');
      setRules(data);
      setError('');
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr?.message || 'Failed to load rules.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  const openAddForm = () => {
    setEditingRule(null);
    setFormName('');
    setFormDescription('');
    setFormRuleType(RULE_TYPES[0]);
    setFormPriority(rules.length + 1);
    setShowForm(true);
  };

  const openEditForm = (rule: AssignmentRule) => {
    setEditingRule(rule);
    setFormName(rule.name);
    setFormDescription(rule.description);
    setFormRuleType(rule.ruleType);
    setFormPriority(rule.priority);
    setShowForm(true);
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingRule(null);
  };

  const handleSave = async () => {
    setError('');
    try {
      if (editingRule) {
        await apiClient(`/batch-assignment/rules/${editingRule.id}`, {
          method: 'PATCH',
          body: { name: formName, description: formDescription },
        });
      } else {
        await apiClient('/batch-assignment/rules', {
          method: 'POST',
          body: {
            name: formName,
            ruleType: formRuleType,
            priority: formPriority,
            description: formDescription,
          },
        });
      }
      setShowForm(false);
      setEditingRule(null);
      await fetchRules();
    } catch (err: unknown) {
      const apiErr = err as { statusCode?: number; message?: string };
      if (apiErr?.statusCode === 409) {
        setError(apiErr?.message || 'Priority conflict. Choose a different priority.');
      } else {
        setError(apiErr?.message || 'Failed to save rule.');
      }
    }
  };

  const handleDelete = async (rule: AssignmentRule) => {
    if (!window.confirm(`Delete rule "${rule.name}"? This cannot be undone.`)) return;
    setError('');
    try {
      await apiClient(`/batch-assignment/rules/${rule.id}`, { method: 'DELETE' });
      await fetchRules();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr?.message || 'Failed to delete rule.');
    }
  };

  const handleToggleEnabled = async (rule: AssignmentRule) => {
    setError('');
    try {
      await apiClient(`/batch-assignment/rules/${rule.id}`, {
        method: 'PATCH',
        body: { enabled: !rule.enabled },
      });
      await fetchRules();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr?.message || 'Failed to update rule.');
    }
  };

  // Drag and drop handlers
  const handleDragStart = (index: number) => {
    setDragIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = async (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    setDragOverIndex(null);

    if (dragIndex === null || dragIndex === dropIndex) {
      setDragIndex(null);
      return;
    }

    // Reorder the array
    const reordered = [...rules];
    const [moved] = reordered.splice(dragIndex, 1);
    reordered.splice(dropIndex, 0, moved);

    // Assign new priorities
    const newOrder = reordered.map((rule, idx) => ({
      id: rule.id,
      priority: idx + 1,
    }));

    // Optimistic update
    setRules(reordered.map((rule, idx) => ({ ...rule, priority: idx + 1 })));
    setDragIndex(null);

    try {
      await apiClient('/batch-assignment/rules/reorder', {
        method: 'PUT',
        body: { rules: newOrder },
      });
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr?.message || 'Failed to reorder rules.');
      await fetchRules();
    }
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  if (loading) {
    return <div style={styles.loading}>Loading rules...</div>;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h3 style={styles.title}>Assignment Rules</h3>
        <button style={styles.addBtn} onClick={openAddForm} aria-label="Add rule">
          + Add Rule
        </button>
      </div>

      {error && <div style={styles.error} role="alert">{error}</div>}

      {showForm && (
        <div style={styles.form}>
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Name</label>
              <input
                style={styles.input}
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="Rule name"
              />
            </div>
            {!editingRule && (
              <>
                <div style={{ ...styles.formGroup, flex: 'none', width: '160px' }}>
                  <label style={styles.label}>Type</label>
                  <select
                    style={styles.select}
                    value={formRuleType}
                    onChange={(e) => setFormRuleType(e.target.value)}
                  >
                    {RULE_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div style={{ ...styles.formGroup, flex: 'none', width: '80px' }}>
                  <label style={styles.label}>Priority</label>
                  <input
                    style={styles.input}
                    type="number"
                    min={1}
                    value={formPriority}
                    onChange={(e) => setFormPriority(Number(e.target.value))}
                  />
                </div>
              </>
            )}
          </div>
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={styles.label}>Description (optional)</label>
            <input
              style={{ ...styles.input, width: '100%', marginTop: '0.25rem' }}
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              placeholder="Brief description of what this rule does"
            />
          </div>
          <div style={styles.formActions}>
            <button
              style={styles.saveBtn}
              onClick={handleSave}
              disabled={!formName.trim()}
            >
              {editingRule ? 'Update' : 'Create'}
            </button>
            <button style={styles.cancelBtn} onClick={cancelForm}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {rules.length === 0 && !showForm && (
        <div style={styles.empty}>
          No assignment rules configured yet. Click "Add Rule" to create your first rule.
        </div>
      )}

      <div role="list" aria-label="Assignment rules">
        {rules.map((rule, index) => (
          <div
            key={rule.id}
            style={{
              ...styles.card,
              ...(dragIndex === index ? styles.cardDragging : {}),
              ...(dragOverIndex === index ? styles.cardDragOver : {}),
              opacity: rule.enabled ? 1 : 0.6,
            }}
            role="listitem"
            draggable
            onDragStart={() => handleDragStart(index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, index)}
            onDragEnd={handleDragEnd}
          >
            <div style={styles.priorityBadge}>{rule.priority}</div>

            <div style={styles.cardContent}>
              <p style={styles.cardName}>{rule.name}</p>
              {rule.description && <p style={styles.cardDesc}>{rule.description}</p>}
              <span style={styles.ruleTypeBadge}>{rule.ruleType}</span>
            </div>

            <div style={styles.cardActions}>
              <button
                style={{
                  ...styles.toggle,
                  backgroundColor: rule.enabled ? PRIMARY : '#ccc',
                }}
                onClick={() => handleToggleEnabled(rule)}
                aria-label={`${rule.enabled ? 'Disable' : 'Enable'} ${rule.name}`}
                title={rule.enabled ? 'Enabled' : 'Disabled'}
              >
                <span
                  style={{
                    ...styles.toggleDot,
                    left: rule.enabled ? '19px' : '3px',
                  }}
                />
              </button>
              <button
                style={styles.iconBtn}
                onClick={() => openEditForm(rule)}
                title="Edit"
                aria-label={`Edit ${rule.name}`}
              >
                ✏️
              </button>
              <button
                style={styles.iconBtn}
                onClick={() => handleDelete(rule)}
                title="Delete"
                aria-label={`Delete ${rule.name}`}
              >
                🗑️
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
